'use client';

import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

import BgImage from '@/assets/images/pages-bg.jpg';
import { BaseDropdown } from '@/components/forms/base-dropdown/BaseDropdown.component';
import { PaperBox } from '@/components/paper-box/PaperBox.component';
import { PodContent } from '@/components/pod-content/PodContent.component';
import { GTMEvent } from '@/features/google-tag-manager/components/GoogleTagManager';
import { GetTermsOfService } from '@/features/pages/api/terms-of-service';

import { BodyContentType } from '../../types';

import styles from './TermsOfServiceContent.module.scss';

interface TermsOfService {
    id: string;
    // documentId is what v5's findOne accepts in the URL path. The list
    // response surfaces it alongside `id`; the detail fetch prefers it
    // post-cutover. While Strapi is still v4, documentId is undefined and
    // we fall back to the numeric id (which v4's findOne accepts).
    documentId?: string;
    title: string;
    description: string;
    published_date: string;
    body: BodyContentType[] | BodyContentType;
}

// Pick the URL path segment for the detail-view fetch:
//   - v5 Strapi: documentId (numeric id is rejected)
//   - v4 Strapi: numeric id (documentId is absent)
// Cast both to string so the dropdown value type stays consistent.
const detailLookup = (terms: TermsOfService) => String(terms.documentId ?? terms.id);

export const TermsOfServiceContent = ({ lang }: { lang: string }) => {
    const [selectedDocumentId, setSelectedDocumentId] = useState<string>('');
    const [termsOfServices, setTermsOfServices] = useState<TermsOfService[]>([]);
    const [selectedTerms, setSelectedTerms] = useState<TermsOfService | null>(null);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const router = useRouter();

    useEffect(() => {
        const fetchListData = async () => {
            const list = await GetTermsOfService({
                language: lang,
            });

            if (list.length === 0) {
                router.push('/not-found');
            }

            setTermsOfServices(list);
            const firstLookup = detailLookup(list[0]);
            setSelectedDocumentId(firstLookup);

            // Fetch the full content for the first terms on initial load
            const firstTerms = await GetTermsOfService({
                language: lang,
                documentId: firstLookup,
            });

            if (firstTerms) {
                setSelectedTerms(firstTerms);
            }

            setIsInitialLoad(false);
        };
        fetchListData();
    }, [lang, router]);

    useEffect(() => {
        // Skip if it's the initial load (already fetched in first useEffect)
        if (isInitialLoad || !selectedDocumentId) return;

        const fetchTermsData = async () => {
            const terms = await GetTermsOfService({
                language: lang,
                documentId: selectedDocumentId,
            });

            if (terms) {
                setSelectedTerms(terms);
            }
        };

        fetchTermsData();
    }, [selectedDocumentId, lang, isInitialLoad]);

    const handleIdChange = (documentId: string) => {
        if (documentId === selectedDocumentId) return;
        setSelectedDocumentId(documentId);
    };

    const dropdownOptions = termsOfServices.map((terms) => ({
        label: new Date(terms.published_date).toLocaleDateString(),
        value: detailLookup(terms),
    }));

    return (
        <>
            <section className={styles.wrapper}>
                <div className={styles.paperBox}>
                    <div className={styles.dateSelector}>
                        <BaseDropdown
                            value={selectedDocumentId}
                            options={dropdownOptions}
                            onChange={handleIdChange}
                            placeholder=""
                            label="Terms of Service Version"
                        />
                    </div>
                    <PaperBox
                        title={selectedTerms?.title}
                        tapeBoxClassName={styles.paperBoxTape}
                        contentClassName={styles.paperBoxContent}
                        vertical
                    >
                        <h4 className={styles.description}>{selectedTerms?.description || ''}</h4>
                        {selectedTerms?.body ? <PodContent content={selectedTerms.body} /> : <></>}
                    </PaperBox>
                </div>
                <Image className={styles.bgImage} src={BgImage} alt="" fill />
            </section>
            <GTMEvent pageTitle="terms-of-service-page" />
        </>
    );
};
